import { useState, useEffect } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import SortableTable from '../components/SortableTable';
import { ChartLine, ChartBar as ChartBarIcon, Cube, TrendUp, TrendDown, CalendarBlank, ArrowsClockwise } from '@phosphor-icons/react';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const COLORS = ['#7d64ff', '#28cd8c', '#f6a609', '#ea4b5a', '#ff7d00', '#6153a7', '#00b8d9', '#36B37E', '#FF5630', '#172B4D'];

const fmt = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n || 0);

function StatCard({ label, value, icon: Icon, color, trend }) {
  return (
    <div className="bg-white border border-[#e5e5ea] rounded-xl p-5" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-[11px] font-bold uppercase tracking-[0.15em] text-[#5a5c87]">{label}</span>
        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}15` }}>
          <Icon size={16} style={{ color }} weight="bold" />
        </div>
      </div>
      <p className="text-xl font-bold text-[#0A0A0A]">{value}</p>
      {trend && <p className={`text-xs font-medium mt-1 ${trend.startsWith('+') ? 'text-[#28cd8c]' : 'text-[#ea4b5a]'}`}>{trend}</p>}
    </div>
  );
}

export default function AnalyticsPage() {
  const [revenueTrends, setRevenueTrends] = useState([]);
  const [categoryPerf, setCategoryPerf] = useState([]);
  const [topProducts, setTopProducts] = useState([]);
  const [heatmap, setHeatmap] = useState([]);
  const [days, setDays] = useState(30);
  const [loadingTrends, setLoadingTrends] = useState(true);
  const [loadingCats, setLoadingCats] = useState(true);
  const [loadingTop, setLoadingTop] = useState(true);
  const [loadingHeat, setLoadingHeat] = useState(true);

  const fetchData = async () => {
    setLoadingTrends(true); setLoadingCats(true); setLoadingTop(true); setLoadingHeat(true);
    // Fire all requests independently
    axios.get(`${API_URL}/api/analytics/revenue-trends?days=${days}`, { withCredentials: true })
      .then(r => setRevenueTrends(r.data)).catch(() => {}).finally(() => setLoadingTrends(false));
    axios.get(`${API_URL}/api/analytics/category-performance`, { withCredentials: true })
      .then(r => setCategoryPerf(r.data)).catch(() => {}).finally(() => setLoadingCats(false));
    axios.get(`${API_URL}/api/analytics/top-products?limit=10`, { withCredentials: true })
      .then(r => setTopProducts(r.data)).catch(() => {}).finally(() => setLoadingTop(false));
    axios.get(`${API_URL}/api/analytics/fulfillment-heatmap?days=${days}`, { withCredentials: true })
      .then(r => setHeatmap(r.data)).catch(() => {}).finally(() => setLoadingHeat(false));
  };

  useEffect(() => { fetchData(); }, [days]);

  const totalRevenue = revenueTrends.reduce((s, d) => s + d.revenue, 0);
  const totalOrders = revenueTrends.reduce((s, d) => s + d.orders, 0);
  const totalDelivered = revenueTrends.reduce((s, d) => s + d.delivered, 0);
  const totalCancelled = revenueTrends.reduce((s, d) => s + d.cancelled, 0);
  const fulfillmentRate = totalOrders > 0 ? Math.round((totalDelivered / totalOrders) * 100) : 0;

  const DAY_ORDER = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const heatmapGrid = DAY_ORDER.map(day => {
    const dayData = { day };
    for (let h = 0; h < 24; h++) {
      const match = heatmap.find(d => d.day === day && d.hour === h);
      dayData[`h${h}`] = match ? match.total : 0;
    }
    return dayData;
  });

  const topProductColumns = [
    { key: 'name', label: 'Product', sortable: true, render: (v, row) => (
      <div>
        <p className="text-sm font-semibold text-[#0A0A0A]">{v}</p>
        <p className="text-[11px] text-[#9c9db7]">{row.sku} {row.category ? `· ${row.category}` : ''}</p>
      </div>
    )},
    { key: 'quantity_sold', label: 'Qty Sold', sortable: true, render: (v) => <span className="font-bold text-sm">{v}</span> },
    { key: 'revenue', label: 'Revenue', sortable: true, render: (v) => <span className="font-bold text-sm">{fmt(v)}</span> },
    { key: 'orders', label: 'Orders', sortable: true },
  ];

  if (loadingTrends) return <DashboardLayout title="Analytics & Insights" subtitle="Advanced business intelligence" actions={
      <div className="flex items-center gap-2">
        <CalendarBlank size={16} className="text-[#5a5c87]" />
        <select value={days} onChange={e => setDays(Number(e.target.value))}
          className="text-sm border border-[#e5e5ea] rounded-lg px-3 py-1.5 outline-none focus:border-[#7d64ff]"
          data-testid="analytics-period-select">
          <option value={7}>Last 7 days</option>
          <option value={14}>Last 14 days</option>
          <option value={30}>Last 30 days</option>
          <option value={60}>Last 60 days</option>
          <option value={90}>Last 90 days</option>
        </select>
      </div>
    }>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">{[1,2,3,4,5].map(i => <div key={i} className="h-24 skeleton rounded-xl" />)}</div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6"><div className="h-80 skeleton rounded-xl" /><div className="h-80 skeleton rounded-xl" /></div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6"><div className="h-64 skeleton rounded-xl" /><div className="h-64 skeleton rounded-xl" /></div>
    </DashboardLayout>;

  return (
    <DashboardLayout title="Analytics & Insights" subtitle="Advanced business intelligence" actions={
      <div className="flex items-center gap-2">
        <CalendarBlank size={16} className="text-[#5a5c87]" />
        <select value={days} onChange={e => setDays(Number(e.target.value))}
          className="text-sm border border-[#e5e5ea] rounded-lg px-3 py-1.5 outline-none focus:border-[#7d64ff]"
          data-testid="analytics-period-select">
          <option value={7}>Last 7 days</option>
          <option value={14}>Last 14 days</option>
          <option value={30}>Last 30 days</option>
          <option value={60}>Last 60 days</option>
          <option value={90}>Last 90 days</option>
        </select>
        <button onClick={fetchData} className="p-2 text-[#5a5c87] hover:text-[#7d64ff] hover:bg-[#f8f7ff] rounded-lg" data-testid="refresh-analytics">
          <ArrowsClockwise size={16} />
        </button>
      </div>
    }>
      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6" data-testid="analytics-summary">
        <StatCard label="Total Revenue" value={fmt(totalRevenue)} icon={ChartLine} color="#7d64ff" />
        <StatCard label="Total Orders" value={totalOrders.toLocaleString()} icon={ChartBarIcon} color="#28cd8c" />
        <StatCard label="Delivered" value={totalDelivered.toLocaleString()} icon={TrendUp} color="#28cd8c" />
        <StatCard label="Cancelled" value={totalCancelled.toLocaleString()} icon={TrendDown} color="#ea4b5a" />
        <StatCard label="Fulfillment" value={`${fulfillmentRate}%`} icon={Cube} color={fulfillmentRate >= 80 ? '#28cd8c' : '#f6a609'} />
      </div>

      {/* Revenue & Orders Trend */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="bg-white border border-[#e5e5ea] rounded-xl p-6" data-testid="revenue-trend-chart">
          <h3 className="font-bold text-base mb-4">Revenue Trend</h3>
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={revenueTrends}>
              <defs>
                <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#7d64ff" stopOpacity={0.15} />
                  <stop offset="95%" stopColor="#7d64ff" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={v => v.slice(5)} />
              <YAxis tick={{ fontSize: 10 }} tickFormatter={v => `₹${(v/1000).toFixed(0)}k`} />
              <Tooltip formatter={(v) => [fmt(v), 'Revenue']} labelFormatter={v => `Date: ${v}`} />
              <Area type="monotone" dataKey="revenue" stroke="#7d64ff" strokeWidth={2} fill="url(#colorRevenue)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white border border-[#e5e5ea] rounded-xl p-6" data-testid="orders-trend-chart">
          <h3 className="font-bold text-base mb-4">Orders & Fulfillment</h3>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={revenueTrends}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={v => v.slice(5)} />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="orders" name="Total" fill="#7d64ff" radius={[3, 3, 0, 0]} />
              <Bar dataKey="delivered" name="Delivered" fill="#28cd8c" radius={[3, 3, 0, 0]} />
              <Bar dataKey="cancelled" name="Cancelled" fill="#ea4b5a" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Category Performance & Top Products */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {loadingCats ? <div className="h-80 skeleton rounded-xl" /> : (
        <div className="bg-white border border-[#e5e5ea] rounded-xl p-6" data-testid="category-perf-chart">
          <h3 className="font-bold text-base mb-4">Category Performance</h3>
          {categoryPerf.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={categoryPerf} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={v => `₹${(v/1000).toFixed(0)}k`} />
                <YAxis type="category" dataKey="category" tick={{ fontSize: 10 }} width={100} />
                <Tooltip formatter={(v) => [fmt(v), 'Revenue']} />
                <Bar dataKey="revenue" fill="#7d64ff" radius={[0, 4, 4, 0]}>
                  {categoryPerf.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : <p className="text-sm text-[#9c9db7] text-center py-12">No category data</p>}
        </div>
        )}

        {loadingCats ? <div className="h-80 skeleton rounded-xl" /> : (
        <div className="bg-white border border-[#e5e5ea] rounded-xl p-6" data-testid="category-pie-chart">
          <h3 className="font-bold text-base mb-4">Revenue Distribution</h3>
          {categoryPerf.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie data={categoryPerf} dataKey="revenue" nameKey="category" cx="50%" cy="50%" outerRadius={100} innerRadius={50}
                  label={({ category, percent }) => `${category}: ${(percent * 100).toFixed(0)}%`} labelLine={{ strokeWidth: 1 }}>
                  {categoryPerf.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(v) => fmt(v)} />
              </PieChart>
            </ResponsiveContainer>
          ) : <p className="text-sm text-[#9c9db7] text-center py-12">No data</p>}
        </div>
        )}
      </div>

      {/* Heatmap */}
      {loadingHeat ? <div className="h-52 skeleton rounded-xl mb-6" /> : heatmap.length > 0 && (
        <div className="bg-white border border-[#e5e5ea] rounded-xl p-6 mb-6" data-testid="heatmap-chart">
          <h3 className="font-bold text-base mb-4">Order Activity Heatmap</h3>
          <div className="overflow-x-auto">
            <div className="min-w-[600px]">
              <div className="flex items-center gap-1 mb-1 pl-10">
                {[0, 3, 6, 9, 12, 15, 18, 21].map(h => (
                  <span key={h} className="text-[9px] text-[#9c9db7]" style={{ width: `${100/8}%` }}>{h}:00</span>
                ))}
              </div>
              {heatmapGrid.map(row => (
                <div key={row.day} className="flex items-center gap-1 mb-0.5">
                  <span className="text-[10px] text-[#5a5c87] font-semibold w-8">{row.day}</span>
                  <div className="flex-1 flex gap-0.5">
                    {Array.from({ length: 24 }, (_, h) => {
                      const val = row[`h${h}`] || 0;
                      const maxVal = Math.max(...heatmap.map(d => d.total), 1);
                      const intensity = val / maxVal;
                      return (
                        <div key={h} className="flex-1 h-6 rounded-sm" title={`${row.day} ${h}:00 - ${val} orders`}
                          style={{ backgroundColor: intensity > 0 ? `rgba(125, 100, 255, ${0.1 + intensity * 0.8})` : '#f5f5f7' }} />
                      );
                    })}
                  </div>
                </div>
              ))}
              <div className="flex items-center gap-2 mt-3 justify-end">
                <span className="text-[10px] text-[#9c9db7]">Less</span>
                {[0.1, 0.3, 0.5, 0.7, 0.9].map(o => (
                  <div key={o} className="w-3 h-3 rounded-sm" style={{ backgroundColor: `rgba(125, 100, 255, ${o})` }} />
                ))}
                <span className="text-[10px] text-[#9c9db7]">More</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Top Products Table */}
      {loadingTop ? <div className="h-64 skeleton rounded-xl" /> : (
      <div className="bg-white border border-[#e5e5ea] rounded-xl p-6" data-testid="top-products-table">
        <h3 className="font-bold text-base mb-4">Top Products by Revenue</h3>
        <SortableTable columns={topProductColumns} data={topProducts} emptyMessage="No product data" pageSize={10} />
      </div>
      )}
    </DashboardLayout>
  );
}
